import { t, Trans } from '@lingui/macro';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { InputNumber } from 'components';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { categoryService, priceSetupSerivice, queryClient } from 'services';
import { PopupController } from 'types/Common';
import { BankFeeType } from 'types/PriceSetup';

type PopupProps = PopupController & {
  bankFee?: BankFeeType;
};

const BankAccountCreatePopup = ({ bankFee, onClose }: PopupProps) => {
  const { enqueueSnackbar } = useSnackbar();
const { control, handleSubmit } = useForm({ mode: 'onChange' });
  const isUpdate = !!bankFee;

  const { data: banks } = useQuery(['categoryService.fetchBanks'], () =>
    categoryService.fetchBanks().then((items) => items.map((item) => ({ ...item, label: item.name }))),
  );

  const { mutate: createBankFee, isLoading } = useMutation(
    isUpdate ? priceSetupSerivice.updateBankFee : priceSetupSerivice.createBankFee,
    {
      onSuccess: () => {
        enqueueSnackbar(t`Successful`);
        queryClient.invalidateQueries('priceSetupSerivice.fetchBankFees');
        onClose();
      },
    },
  );

  const handleClickSubmit = () => {
    handleSubmit((values: { name: string; fee: number }) => {
      createBankFee({
        ...values,
        id: bankFee?.id,
        bankId: banks?.find((item) => item.name === values.name)?.id!,
      });
    })();
  };

  return (
    <>
      <DialogTitle>
        <Trans>Bank Collection Fee</Trans>
      </DialogTitle>

      <DialogContent>
        <Grid container columnSpacing={2} rowSpacing={3}>
          <Grid item sm={12}>
            <Controller
              name='name'
              defaultValue={isUpdate ? bankFee?.bank.name : null}
              control={control}
              rules={{ required: t`Bank is required` }}
              render={({ field: { value, onChange }, fieldState: { invalid, error } }) =>
                isUpdate ? (
                  <TextField value={value} fullWidth required label={t`Bank`} InputProps={{ readOnly: true }} />
                ) : (
                  <Autocomplete
                    value={value}
                    onChange={(event, item) => onChange({ target: { value: item.name } })}
                    options={banks ?? []}
                    disableClearable
                    isOptionEqualToValue={(option, value) => option === value || option.label === value}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        fullWidth
                        required
                        label={t`Bank`}
                        error={invalid}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )
              }
            />
          </Grid>
          <Grid item sm={12}>
            <Controller
              name='fee'
              defaultValue={isUpdate ? bankFee?.fee : ''}
              control={control}
              rules={{ required: t`Fee is required` }}
              render={({ field, fieldState: { invalid, error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t`Fee (VND)`}
                  error={invalid}
                  helperText={error?.message}
                  InputProps={{
                    inputComponent: InputNumber as any,
                    inputProps: { thousandSeparator: '.', decimalSeparator: ',' },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <LoadingButton variant='outlined' onClick={onClose}>
          <Trans>Cancel</Trans>
        </LoadingButton>
        <LoadingButton variant='contained' loading={isLoading} onClick={handleClickSubmit}>
          {isUpdate ? t`Update` : t`Create`}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default BankAccountCreatePopup;
